/**
 * 标签页和工作流上下文管理
 *
 * 这个文件负责管理标签页和工作流相关的状态和操作，包括：
 * - 工作流状态管理
 * - 标签页状态管理
 * - 工作流保存和上传
 * - 节点和边的粘贴操作
 * - 工作流下载功能
 * - 版本管理
 */

import cloneDeep from 'lodash-es/cloneDeep'
import { ReactNode, createContext, useContext, useState } from 'react'
import { addEdge } from 'reactflow'
import { updateFlowApi } from '../controllers/API/flow'
import { captureAndAlertRequestErrorHoc } from '../controllers/request'
import { APIClassType, APITemplateType } from '../types/api'
import { FlowType, FlowVersionItem, NodeType } from '../types/flow'
import { TabsContextType, TabsState } from '../types/tabs'
import { generateUUID, updateTemplate } from '../utils'
import { alertContext } from './alertContext'
import { typesContext } from './typesContext'

/**
 * 标签页上下文的初始值
 */
const TabsContextInitialValue: TabsContextType = {
  flow: null,
  tabsState: {}, // keyform isPending
  setFlow: (ac, f) => {},
  setTabsState: (state: TabsState) => {},
  saveFlow: async (flow: FlowType) => Promise.resolve(),
  uploadFlow: () => {},
  setTweak: (tweak: any) => {},
  getTweak: [],
  // 跨组件粘贴
  lastCopiedSelection: null,
  setLastCopiedSelection: (selection: any) => {},
  downloadFlow: (flow: FlowType) => {},
  getNodeId: (nodeType: string) => '',
  paste: (
    selection: { nodes: any; edges: any },
    position: { x: number; y: number; paneX?: number; paneY?: number }
  ) => {},
  version: null,
  setVersion: (version: FlowVersionItem | null) => ''
}

// 创建标签页上下文
export const TabsContext = createContext<TabsContextType>(TabsContextInitialValue)

/**
 * 标签页提供者组件
 *
 * @param children - 子组件
 * @returns 提供标签页和工作流相关状态和方法的上下文提供者
 */
export function TabsProvider({ children }: { children: ReactNode }) {
  const [flow, setFlow] = useState<FlowType>(null)
  const [version, setVersion] = useState<FlowVersionItem | null>(null)
  // flowid: formKeysData
  const [tabsState, setTabsState] = useState<TabsState>({})
  const [lastCopiedSelection, setLastCopiedSelection] = useState(null)
  const [getTweak, setTweak] = useState([])

  const { setErrorData, setNoticeData } = useContext(alertContext)
  const { templates, reactFlowInstance } = useContext(typesContext)

  /**
   * 保存工作流
   * @param flow - 要保存的工作流对象
   * @returns Promise，保存后的工作流对象
   */
  async function saveFlow(flow: FlowType) {
    // 调用保存 API
    const newFlow = await captureAndAlertRequestErrorHoc(updateFlowApi(flow))
    if (!newFlow) return null
    console.log('action :>> ', 'save')
    setFlow(newFlow)
    setTabsState(prev => {
      return {
        ...prev,
        [newFlow.id]: {
          ...prev[newFlow.id],
          isPending: false
        }
      }
    })
    return newFlow
  }

  /**
   * 上传工作流文件
   * 创建文件输入并监听更改事件以上传 JSON 工作流文件。
   * 如果文件类型是 application/json，则读取文件并解析为 JSON 对象。
   * 生成的 JSON 对象传递给 addFlow 函数。
   * @param file - 可选的文件对象，如果提供则直接处理该文件
   */
  function uploadFlow(file?: File) {
    if (file) {
      file.text().then(text => {
        // 将文本解析为 JSON 对象
        const flow: FlowType = JSON.parse(text)
        // 粘贴到画布
        paste({ nodes: flow.data.nodes, edges: flow.data.edges }, { x: 10, y: 10 }, true)
        // 覆盖
        // setFlow(flow);
      })
    } else {
      // 创建文件输入
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.json'
      // 为文件输入添加更改事件监听器
      input.onchange = (e: Event) => {
        // 检查文件类型是否为 application/json
        if ((e.target as HTMLInputElement).files[0].type === 'application/json') {
          // 从文件输入获取文件
          const currentfile = (e.target as HTMLInputElement).files[0]
          // 将文件读取为文本
          currentfile.text().then(text => {
            // 将文本解析为 JSON 对象
            const flow: FlowType = JSON.parse(text)
            // 粘贴到画布
            paste({ nodes: flow.data.nodes, edges: flow.data.edges }, { x: 10, y: 10 }, true)
          })
        }
      }
      // 触发文件输入点击事件以打开文件对话框
      input.click()
    }
  }

  /**
   * 生成节点ID
   * @param nodeType - 节点类型
   * @returns 生成的唯一节点ID
   */
  function getNodeId(nodeType: string) {
    return nodeType + '-' + generateUUID(5)
  }

  /**
   * 下载当前工作流为 JSON 文件
   * @param flow - 要下载的工作流对象
   * @param flowName - 工作流名称
   * @param flowDescription - 工作流描述（可选）
   */
  function downloadFlow(flow: FlowType, flowName: string, flowDescription?: string) {
    // 使用当前工作流数据创建数据 URI
    const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
      JSON.stringify({ ...flow, name: flowName, description: flowDescription })
    )}`

    // 创建链接元素并设置其属性
    const link = document.createElement('a')
    link.href = jsonString
    link.download = `${flowName || flow.name}.json`

    // 模拟点击链接元素以触发下载
    link.click()
    setNoticeData({
      title: '警告：关键数据，JSON 文件可能包含 API 密钥。'
    })
  }

  /**
   * 粘贴节点和边到画布
   * @param selectionInstance - 包含要粘贴的节点和边的选择实例
   * @param position - 粘贴位置坐标
   * @param keepId - 是否保留原始ID（默认为 false）
   */
  function paste(
    selectionInstance,
    position: { x: number; y: number; paneX?: number; paneY?: number },
    keepId: boolean = false // keep id
  ) {
    let minimumX = Infinity
    let minimumY = Infinity
    const idsMap = {}
    let nodes = reactFlowInstance.getNodes()
    let edges = reactFlowInstance.getEdges()
    selectionInstance.nodes.forEach(n => {
      if (n.position.y < minimumY) {
        minimumY = n.position.y
      }
      if (n.position.x < minimumX) {
        minimumX = n.position.x
      }
    })

    const insidePosition = position.paneX
      ? { x: position.paneX + position.x, y: position.paneY + position.y }
      : reactFlowInstance.project({ x: position.x, y: position.y })

    selectionInstance.nodes.forEach((n: NodeType) => {
      // Generate a unique node ID
      let newId = getNodeId(n.data.type)
      // 保留原id； 重复 id除外
      if (keepId && !nodes.find(node => node.id === n.id)) {
        newId = n.id
      }
      idsMap[n.id] = newId

      // Create a new node object
      const newNode: NodeType = {
        id: newId,
        type: 'genericNode',
        position: {
          x: insidePosition.x + n.position.x - minimumX,
          y: insidePosition.y + n.position.y - minimumY
        },
        data: {
          ...cloneDeep(n.data),
          id: newId
        }
      }

      // Add the new node to the list of nodes in state
      nodes = nodes.map(e => ({ ...e, selected: false })).concat({ ...newNode, selected: false })
    })
    console.log(nodes)
    reactFlowInstance.setNodes(nodes)

    selectionInstance.edges.forEach(e => {
      const source = idsMap[e.source]
      const target = idsMap[e.target]
      const sourceHandleSplitted = e.sourceHandle.split('|')
      const sourceHandle = sourceHandleSplitted[0] + '|' + source + '|' + sourceHandleSplitted.slice(2).join('|')
      const targetHandleSplitted = e.targetHandle.split('|')
      const targetHandle = targetHandleSplitted.slice(0, -1).join('|') + '|' + target
      const id = 'reactflow__edge-' + source + sourceHandle + '-' + target + targetHandle
      edges = addEdge(
        {
          source,
          target,
          sourceHandle,
          targetHandle,
          id,
          style: { stroke: '#555' },
          className: targetHandle.split('|')[0] === 'Text' ? 'stroke-gray-800 ' : 'stroke-gray-900 ',
          animated: targetHandle.split('|')[0] === 'Text',
          selected: false
        },
        edges.map(e => ({ ...e, selected: false }))
      )
    })
    reactFlowInstance.setEdges(edges)
  }

  // --
  function updateNodeEdges(flow: FlowType, node: NodeType, template: APIClassType) {
    flow.data.edges.forEach(edge => {
      if (edge.source === node.id) {
        edge.sourceHandle = edge.sourceHandle.split('|').slice(0, 2).concat(template.base_classes).join('|')
      }
    })
  }

  function processFlowEdges(flow) {
    if (!flow.data || !flow.data.edges) return
    flow.data.edges.forEach(edge => {
      edge.className = ''
      edge.style = { stroke: '#555' }
    })
  }

  function processFlowNodes(flow) {
    if (!flow.data || !flow.data.nodes) return
    flow.data.nodes.forEach((node: NodeType) => {
      const template = templates[node.data.type]
      if (!template) {
        // setErrorData({ title: `Unknown node type: ${node.data.type}` });
        console.warn(`Unknown node type: ${node.data.type}`)
        return
      }
      if (Object.keys(template.template).length > 0) {
        node.data.node.display_name = template.display_name || node.data.type
        node.data.node.base_classes = template.base_classes
        node.data.node.description = template.description
        node.data.node.documentation = template.documentation
        updateNodeEdges(flow, node, template)
        node.data.node.template = updateTemplate(
          template.template as unknown as APITemplateType,
          node.data.node.template as APITemplateType
        )
      }
    })
  }

  // 上线版本的版本 id
  const [onlineVid, setOnlineVid] = useState(0)
  const updateOnlineVid = (vid: number) => {
    setOnlineVid(flow.status === 2 ? vid : 0)
  }

  return (
    <TabsContext.Provider
      value={{
        flow,
        setFlow: (action, flow) => {
          console.log('action :>> ', action)
          if (action === 'flow_init') {
            // 按模板矫正数据格式
            processFlowEdges(flow)
            processFlowNodes(flow)
          }

          setFlow(flow)
        },
        saveFlow,
        getTweak,
        setTweak,
        lastCopiedSelection,
        setLastCopiedSelection,
        downloadFlow,
        uploadFlow,
        getNodeId,
        tabsState,
        setTabsState,
        paste,
        version,
        setVersion,
        isOnlineVersion: () => version.id === onlineVid,
        updateOnlineVid
      }}
    >
      {children}
    </TabsContext.Provider>
  )
}
